import { useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { EmptyState, NodeAvatar, showToast } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { Markdown } from "../Markdown";
import {
  useListFiles,
  useUploadFile,
  useArchiveFile,
  useRestoreFile,
  useDeleteFile,
  fetchFile,
} from "../../hooks/use-list-files";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useIsOwner } from "../../hooks/use-is-owner";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteRowModal } from "./ConfirmDeleteRowModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import { NewFolderModal } from "../scope/NewFolderModal";
import type { FileMetadata, GetFileResult } from "../../../lib/types";

interface FilesPanelProps {
  /** Node whose folders + files we list. */
  nodeId: string;
  /** Hosting node's display name — shown as the New-folder parent context. */
  nodeName?: string;
}

type PreviewKind = "markdown" | "text" | "image" | "binary";

function extOf(filename: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(filename);
  return m ? m[1]!.toLowerCase() : "";
}

function fileIcon(file: FileMetadata): string {
  const ext = extOf(file.filename);
  const mime = file.mime_type ?? "";
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
    return ext === "png" ? "ti-file-type-png" : ext === "jpg" || ext === "jpeg" ? "ti-file-type-jpg" : "ti-photo";
  }
  if (ext === "pdf") return "ti-file-type-pdf";
  if (ext === "docx" || ext === "doc") return "ti-file-type-docx";
  if (ext === "csv") return "ti-file-type-csv";
  if (ext === "txt") return "ti-file-type-txt";
  if (ext === "md" || ext === "markdown") return "ti-markdown";
  return "ti-paperclip";
}

function previewKind(file: FileMetadata): PreviewKind {
  const ext = extOf(file.filename);
  const mime = file.mime_type ?? "";
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "image";
  if (ext === "md" || ext === "markdown") return "markdown";
  if (mime.startsWith("text/") || ["txt", "csv", "json", "yaml", "yml", "log", "xml", "html"].includes(ext)) return "text";
  return "binary";
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function FilesPanel({ nodeId, nodeName }: FilesPanelProps) {
  const { currentWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useShowArchived("files", nodeId);
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const files = useListFiles(nodeId, showArchived);
  const upload = useUploadFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ file: FileMetadata; result: GetFileResult } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);

  const items = files.data?.files ?? [];

  // Sprint 5w: folders + files share one panel — both are "organisation" in the
  // user model. Folders come from the library tree (parent_id match); files from
  // the file index. Render order is folders first, files below.
  const folders = useMemo(() => {
    return (tree.data ?? [])
      .filter((n) => n.parent_id === nodeId && n.type === "folder")
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }, [tree.data, nodeId]);

  // Navigable child counts per parent (agents + folders, the tree's contents).
  const childCountByParent = useMemo(() => {
    const m = new Map<string, number>();
    for (const n of tree.data ?? []) {
      if (n.parent_id) m.set(n.parent_id, (m.get(n.parent_id) ?? 0) + 1);
    }
    return m;
  }, [tree.data]);

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    for (const file of Array.from(fileList)) {
      try {
        const meta = await upload.mutateAsync({ file, entityRef: nodeId });
        showToast(`Uploaded ${meta.filename}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        showToast(`Couldn't upload ${file.name}: ${msg}`, "error", true);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  async function openFile(file: FileMetadata) {
    const kind = previewKind(file);
    setOpening(file.id);
    try {
      if (kind === "binary") {
        const result = await fetchFile(file.id, "url");
        if (result.ok && result.delivery === "url") {
          window.open(result.url, "_blank", "noopener");
        }
        return;
      }
      // Text / markdown / image — pull bytes inline so we can render them.
      const result = await fetchFile(file.id, "inline");
      if (result.ok) {
        setPreview({ file, result });
      } else {
        showToast(`Couldn't open ${file.filename}`, "error", true);
      }
    } catch {
      showToast(`Couldn't open ${file.filename}`, "error", true);
    } finally {
      setOpening(null);
    }
  }

  const hasFolders = folders.length > 0;
  const hasFiles = items.length > 0;
  // The title is a static capability label, not a description of current
  // contents. A content-driven title ("Folders" when only folders exist) reads
  // as "files aren't supported here" — but every node accepts both New folder
  // and Upload. (5w anti-pattern #3: titles name the concept, not the context.)
  const panelTitle = "Folders & files";

  const action = (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <ShowArchivedChip value={showArchived} onChange={setShowArchived} />
      <button onClick={() => setNewFolderOpen(true)} style={actionButtonStyle}>
        <i className="ti ti-folder-plus" style={{ fontSize: 13 }} aria-hidden="true" />
        New folder
      </button>
      <button onClick={() => inputRef.current?.click()} disabled={upload.isPending} style={actionButtonStyle}>
        <i className={`ti ${upload.isPending ? "ti-loader-2" : "ti-upload"}`} style={{ fontSize: 13 }} aria-hidden="true" />
        {upload.isPending ? "Uploading…" : "Upload"}
      </button>
    </div>
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Panel
        title={panelTitle}
        count={folders.length + items.length}
        action={action}
        actionsBelowTitle
        isLoading={files.isLoading || tree.isLoading}
        error={files.error ?? tree.error}
        onRetry={() => { files.refetch(); tree.refetch(); }}
        isEmpty={!hasFolders && !hasFiles}
        empty={
          showArchived ? (
            <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
          ) : (
            <EmptyState>
              <div>No folders or files yet.</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
                <button onClick={() => setNewFolderOpen(true)} style={actionButtonStyle}>
                  <i className="ti ti-folder-plus" style={{ fontSize: 13 }} aria-hidden="true" />
                  New folder
                </button>
                <button onClick={() => inputRef.current?.click()} style={actionButtonStyle}>
                  <i className="ti ti-upload" style={{ fontSize: 13 }} aria-hidden="true" />
                  Upload file
                </button>
              </div>
            </EmptyState>
          )
        }
      >
        {folders.map((folder) => {
          const childCount = childCountByParent.get(folder.id) ?? 0;
          return (
            <PanelRow
              key={folder.id}
              onClick={() => navigate(`/agents/${folder.id}`)}
              title={folder.name}
              trailing={
                childCount > 0 ? (
                  <span style={countBadgeStyle}>{childCount} item{childCount === 1 ? "" : "s"}</span>
                ) : undefined
              }
            >
              <NodeAvatar type="folder" size="sm" name={folder.name} />
              <span style={nameStyle}>{folder.name}</span>
            </PanelRow>
          );
        })}

        {hasFolders && hasFiles && <div style={filesDividerStyle}>Files</div>}

        {items.map((file) => (
          <FileRow
            key={file.id}
            file={file}
            opening={opening === file.id}
            onOpen={() => openFile(file)}
          />
        ))}
      </Panel>

      {preview && (
        <FilePreviewModal
          file={preview.file}
          result={preview.result}
          onClose={() => setPreview(null)}
        />
      )}

      <NewFolderModal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        parentId={nodeId}
        parentName={nodeName}
        onCreated={(node) => navigate(`/agents/${node.id}`)}
      />
    </>
  );
}

/**
 * Sprint 14g — one file row, owning its own archive/restore/delete wiring and
 * confirm-delete modal state (mirrors RoutineRow). Folders are handled inline
 * above and keep their existing archive-node UX; only files get this menu.
 */
function FileRow({
  file,
  opening,
  onOpen,
}: {
  file: FileMetadata;
  opening: boolean;
  onOpen: () => void;
}) {
  const archiveFile = useArchiveFile();
  const restoreFile = useRestoreFile();
  const deleteFile = useDeleteFile();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isArchived = !!file.archived_at;

  function handleArchive() {
    archiveFile.mutate(file.id, {
      onSuccess: () =>
        showToast(`Archived '${file.filename}'`, "success", false, {
          label: "Undo",
          onClick: () => restoreFile.mutate(file.id),
        }, 10000),
      onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
    });
  }

  function handleRestore() {
    restoreFile.mutate(file.id, {
      onSuccess: () => showToast(`Restored '${file.filename}'`),
      onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
    });
  }

  return (
    <>
      <PanelRow
        onClick={onOpen}
        title={file.filename}
        trailing={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={timeStyle}>
              {opening ? "opening…" : `${formatSize(file.size)} · ${formatRelativeTime(file.created_at)}`}
            </span>
            <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
              <PanelRowMenu
                isArchived={isArchived}
                canDelete={canDelete}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onDelete={() => setConfirmDelete(true)}
              />
            </span>
          </span>
        }
      >
        <i className={`ti ${fileIcon(file)}`} style={{ fontSize: 16, color: "var(--color-text-tertiary)", flexShrink: 0, opacity: isArchived ? 0.6 : 1 }} aria-hidden="true" />
        <span style={{ ...nameStyle, opacity: isArchived ? 0.6 : 1 }}>{file.filename}</span>
        {isArchived && <ArchivedBadge />}
      </PanelRow>

      <ConfirmDeleteRowModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        entityKind="file"
        entityName={file.filename}
        entityId={file.id}
        backRefKind="file"
        onConfirm={async ({ force }) => {
          await deleteFile.mutateAsync({ id: file.id, force });
          showToast(`Deleted '${file.filename}'`);
        }}
      />
    </>
  );
}

function FilePreviewModal({
  file,
  result,
  onClose,
}: {
  file: FileMetadata;
  result: GetFileResult;
  onClose: () => void;
}) {
  const kind = previewKind(file);

  let bodyEl;
  if (!result.ok) {
    bodyEl = <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Couldn't load this file.</div>;
  } else if (kind === "image") {
    const src = result.delivery === "inline"
      ? `data:${file.mime_type};base64,${result.content}`
      : result.delivery === "url" ? result.url : "";
    bodyEl = <img src={src} alt={file.filename} style={{ maxWidth: "100%", borderRadius: "var(--border-radius-sm)" }} />;
  } else if (result.delivery === "inline") {
    bodyEl = kind === "markdown"
      ? <Markdown>{result.content}</Markdown>
      : <pre style={preStyle}>{result.content}</pre>;
  } else {
    bodyEl = <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Preview unavailable.</div>;
  }

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={file.filename} style={modalBox}>
        <div style={modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <i className={`ti ${fileIcon(file)}`} style={{ fontSize: 16, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
            <span style={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {file.filename}
            </span>
          </div>
          <button onClick={onClose} aria-label="Close" style={closeStyle}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>
        <div style={modalBody}>{bodyEl}</div>
      </div>
    </div>
  );
}

const actionButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const countBadgeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};

// Sprint 5w: faint "Files" subheading separating files from the folders above.
const filesDividerStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "10px 8px 2px",
  marginTop: 4,
  borderTop: "var(--kuvio-border-default)",
};

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
  padding: 24,
};

const modalBox: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  maxWidth: 680,
  width: "100%",
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "14px 16px",
  borderBottom: "var(--kuvio-border-default)",
};

const modalBody: CSSProperties = {
  padding: 16,
  overflowY: "auto",
};

const preStyle: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 12,
  color: "var(--color-text-secondary)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
};

const closeStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 2,
  display: "flex",
};
